import Modal from "react-modal";
import { toast } from "react-toastify";
import "./DocumentCategory.css";
import "react-toastify/dist/ReactToastify.css";
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Delete from "../../../../images/icons/delete.svg";
import React, { useEffect, useState, useRef } from "react";
import { logout, reset } from "../../../../features/auth/authSlice";
import addicon from "../../../../images/team-member-tab/add-icon.svg";
import crssrImage from "../../../../images/singleinvoice/cross-red.svg";
import editimgblue from "../../../../images/team-member-tab/edit-btn-blue.svg";
import LeftArrow from "../../../../images/icons/blue-left-arrow.svg";
import { setHeaderText } from "../../../../features/headerText/headerTextSlice";
import { reset as invoiceReset } from "../../../../features/invoice/invoiceSlice.js";
import { reset as headerReset } from "../../../../features/headerText/headerTextSlice.js";
import { reset as appSettingsReset } from "../../../../features/appSettings/appSettingsSlice.js";
import { useCreateDocumentCategory, useDeleteDocumentCategory, useFetchDocumentCategories, useUpdateDocumentCategory } from "../DocumentManagement/DocumentCategoryApi.jsx";

const DocumentCategory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [documentCategoryCreate, setDocumentCategoryCreate] = useState("");
  const documentCategoryCreateRef = useRef(null);
  const [documentCategoryEdit, setDocumentCategoryEdit] = useState("");
  const [updatedDocumentCategories, setUpdatedDocumentCategories] = useState([]);
  const [selectedSortOption, setSelectedSortOption] = useState(null);
  const [checkedOption, setCheckedOption] = useState(null);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef(null);
  const [filter, setFilter] = useState("");

  let token = null;
  const { user } = useSelector((state) => state.auth);
  if (user && user.token) {
    token = user.token;
  }

  const { data: documentCategories = [], isLoading, isError } = useFetchDocumentCategories(token);

  const deleteDocumentCategoryMutation = useDeleteDocumentCategory(token);
  const createDocumentCategoryMutation = useCreateDocumentCategory(token);
  const updateDocumentCategoryMutation = useUpdateDocumentCategory(token);

  useEffect(() => {
    dispatch(setHeaderText("Document Category"));
    setUpdatedDocumentCategories(documentCategories?.data);
  }, [documentCategories]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        document.activeElement.blur();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const selectSortOption = (option) => {
    setSelectedSortOption(option);
    setCheckedOption(option);
    setSortDropdownOpen(false);
  };

  const toggleSortDropdown = () => {
    setSortDropdownOpen(!sortDropdownOpen);
  };

  const sortData = (data, sortOption) => {
    if (!Array.isArray(data)) {
      return [];
    }
    switch (sortOption) {
      case 'By name: A - Z':
        return data.slice().sort((a, b) => a.document_category.localeCompare(b.document_category));
      case 'By name: Z - A':
        return data.slice().sort((a, b) => b.document_category.localeCompare(a.document_category));
      case 'Date: old to new':
        return data.slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'Date: new to old':
        return data.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      default:
        return data;
    }
  };

  const sortedData = sortData(updatedDocumentCategories || [], selectedSortOption);

  const filteredData = sortedData.filter((item) => {
    const searchTerm = filter.trim()?.toLowerCase();
    return item.document_category?.toLowerCase().includes(searchTerm);
  });

  const handleOpenModal = (item) => {
    setIsModalOpen(true);
    setSelectedId(item._id);
    setDocumentCategoryEdit(item.document_category);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedId("");
    setDocumentCategoryEdit("");
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsSubmitLoading(true);
      await deleteDocumentCategoryMutation.mutateAsync(selectedId);
      setUpdatedDocumentCategories((prevState) => prevState.filter((category) => category._id !== selectedId));
      toast.success("Document category removed successfully", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      global.config.activityLog(window.location.href, 'Document Category', `Document Category (${selectedId}) removed successfully`);
      handleCloseModal();
    } catch (error) {
      toast.error("Something went wrong", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      global.config.slackMessage(error.toString());
      if (error.response?.data?.status === "deleted") {
        localStorage.removeItem("user");
        dispatch(logout());
        dispatch(reset());
        dispatch(invoiceReset());
        dispatch(headerReset());
        dispatch(appSettingsReset());
        toast.error('This user is deleted!', {
          position: "bottom-right",
          autoClose: 1500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        navigate("/");
        return;
      }
    } finally {
      setIsSubmitLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    if (documentCategoryCreate === "") {
      toast.error("Document category cannot be empty!", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      documentCategoryCreateRef.current.focus();
      return;
    }
    const requestData = {
      document_category: documentCategoryCreate,
    };
    try {
      setIsSubmitLoading(true);
      const data = await createDocumentCategoryMutation.mutateAsync(requestData);
      if (data.status === "error") {
        toast.error("Something went wrong", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        global.config.activityLog(window.location.href, 'Document Category', `Something went wrong while adding Document Category ${documentCategoryCreate}`);
      } else if (data.status === "exists") {
        toast.error("This document category already exists", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        global.config.activityLog(window.location.href, 'Document Category', `Document Category ${documentCategoryCreate} already exists`);
      } else {
        toast.success("Document category added successfully", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        global.config.activityLog(window.location.href, 'Document Category', `Document Category ${documentCategoryCreate} added successfully`);
        setDocumentCategoryCreate("");
      }
    } catch (error) {
      global.config.slackMessage(error.toString());
      if (error.response?.data?.status === "deleted") {
        localStorage.removeItem("user");
        dispatch(logout());
        dispatch(reset());
        dispatch(invoiceReset());
        dispatch(headerReset());
        dispatch(appSettingsReset());
        toast.error('This user is deleted!', {
          position: "bottom-right",
          autoClose: 1500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        navigate("/");
        return;
      }
      toast.error("Something went wrong", {
        position: "bottom-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      global.config.activityLog(window.location.href, 'Document Category', `Something went wrong`);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitLoading(true);
      const response = await updateDocumentCategoryMutation.mutateAsync({
        selectedId,
        document_category: documentCategoryEdit,
      });
      const data = response.data;
      if (data.status === "error") {
        toast.error("Something went wrong", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else if (data.status === "exists") {
        toast.error("Document category already exists", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else {
        setUpdatedDocumentCategories((prevState) => {
          const updatedCategories = prevState.map((item) => {
            if (item._id === selectedId) {
              return { ...item, document_category: documentCategoryEdit };
            }
            return item;
          });
          return updatedCategories;
        });
        toast.success("Document category updated successfully", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        global.config.activityLog(window.location.href, 'Document Category', `Document Category ${documentCategoryEdit} updated successfully`);
        handleCloseModal();
      }
    } catch (error) {
      global.config.slackMessage(error.toString());
      if (error.response?.data?.status === "deleted") {
        localStorage.removeItem("user");
        dispatch(logout());
        dispatch(reset());
        dispatch(invoiceReset());
        dispatch(headerReset());
        dispatch(appSettingsReset());
        toast.error('This user is deleted!', {
          position: "bottom-right",
          autoClose: 1500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        navigate("/");
        return;
      }
      toast.error("Something went wrong", {
        position: "bottom-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleClickOutsideDropdown = (event) => {
    if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
      setSortDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutsideDropdown);
    return () => {
      document.removeEventListener('click', handleClickOutsideDropdown);
    };
  }, []);

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
      <div className='tab-outer-time'>
        <ul className="tabs">
          <li>
            <Link to={`/user/documents/categories`} className={window.location.pathname.startsWith('/user/documents/categories') ? 'active' : ''}>
              Categories
            </Link>
          </li>
        </ul>

        <div className="tabs-content">
          <div className='div-outter-for-department'>
            <div className="text-arrow-outer">
              <div className="text-arrow-inner">
                <Link to={`/user/documents/overview`}>
                  <div className="text-arrow">
                    <div className="left-arrow">
                      <img src={LeftArrow} alt="left-arrow" />
                    </div>
                    <div className="top-text">
                      Back to Documents
                    </div>
                  </div>
                </Link>
              </div>
            </div>
            <div className='add-department-section top-filter-client-inner'>
              <div className="top-filter-client-inner-left">
                <input
                  type='text'
                  placeholder='Add new document category'
                  value={documentCategoryCreate}
                  onChange={(e) => setDocumentCategoryCreate(e.target.value)}
                  ref={documentCategoryCreateRef}
                />
                <button className='add-btn-in-department-b1' onClick={handleCreateSubmit} disabled={isSubmitLoading}>
                  {isSubmitLoading ? (
                    <div
                      style={{
                        width: "21px",
                        height: "21px",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        borderRadius: "4px",
                        animation: "pulse 1.5s ease-in-out infinite",
                      }}
                    />
                  ) : (
                    <img src={addicon} alt="add" />
                  )}
                </button>
              </div>

              <div className="top-filter-client-sort">
                <div className="custom-dropdown1">
                  <div
                    ref={sortDropdownRef}
                    className={`custom-dropdown-user-new-outer custom-dropdown-select ${sortDropdownOpen ? 'open' : ''}`}
                  >
                    <div className="selected-option custom-dropdown-user-new" onClick={toggleSortDropdown}>
                      <div className="center-sort">
                        {selectedSortOption ? selectedSortOption : 'Sort By'}
                      </div>
                    </div>
                    {sortDropdownOpen && (
                      <div className="custom-dropdown-new">
                        <ul className="type-list">
                          <li
                            className={checkedOption === 'By name: A - Z' ? 'selected' : ''}
                            onClick={() => selectSortOption('By name: A - Z')}
                          >
                            <span className={`checkbox ${checkedOption === 'By name: A - Z' ? 'checked' : ''}`}></span>
                            By name: A - Z
                          </li>
                          <li
                            className={checkedOption === 'By name: Z - A' ? 'selected' : ''}
                            onClick={() => selectSortOption('By name: Z - A')}
                          >
                            <span className={`checkbox ${checkedOption === 'By name: Z - A' ? 'checked' : ''}`}></span>
                            By name: Z - A
                          </li>
                          <li
                            className={checkedOption === 'Date: old to new' ? 'selected' : ''}
                            onClick={() => selectSortOption('Date: old to new')}
                          >
                            <span className={`checkbox ${checkedOption === 'Date: old to new' ? 'checked' : ''}`}></span>
                            Date: old to new
                          </li>
                          <li
                            className={checkedOption === 'Date: new to old' ? 'selected' : ''}
                            onClick={() => selectSortOption('Date: new to old')}
                          >
                            <span className={`checkbox ${checkedOption === 'Date: new to old' ? 'checked' : ''}`}></span>
                            Date: new to old
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                <div className="top-filter-client-sort-search">
                  <div className="top-filter-client-search">
                    <div className="search-sec">
                      <div className="search-container">
                        <input
                          type="text"
                          className="search-input"
                          placeholder="Search by document category name"
                          onChange={(e) => setFilter(e.target.value)}
                          value={filter}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='department-section-listing'>
              {isLoading ? (
                <>
                  {Array.from({ length: 12 }).map((_, index) => (
                    <div className='grid-item' key={`skeleton-${index}`} style={{ pointerEvents: 'none' }}>
                      <div
                        style={{
                          width: "100%",
                          height: "40px",
                          backgroundColor: "#e5e7eb",
                          borderRadius: "4px",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {filteredData.map((item) => (
                    <div className='grid-item' key={item._id}>
                      <input type='text' className='input-tag' value={item.document_category} readOnly />
                      <button className='edit-button' onClick={() => handleOpenModal(item)}>
                        <img src={editimgblue} alt="edit" />Edit
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
          <div className="modal-invite-main-outter">
            <Modal
              className="client-modal leave"
              isOpen={isModalOpen}
              onRequestClose={handleCloseModal}
              contentLabel="Edit Document Category"
              style={{
                content: {
                  height: "41%",
                  width: "30%",
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  parent: document.querySelector(".admin-outer.time.tracker"),
                },
              }}
            >
              <div className="modal">
                <div className="modal-close-invite outter-modal-main">
                  <a onClick={handleCloseModal}>
                    <img src={crssrImage} alt="Close" />
                  </a>
                </div>
                <div className="modal-client-head">
                  <h2>Edit Document Category</h2>
                </div>
                <div className="form-field-client-outer">
                  <form onSubmit={handleUpdateSubmit}>
                    <div className="form-field-client">
                      <label htmlFor="categoryName" className="form-label">
                        Name of Document Category
                      </label>
                      <input
                        type="text"
                        id="categoryName"
                        name="categoryName"
                        className="form-input"
                        value={documentCategoryEdit}
                        onChange={(e) => setDocumentCategoryEdit(e.target.value)}
                        required
                      />
                    </div>
                    {isSubmitLoading ? (
                      <div className="form-field-client-btns">
                        <div
                          style={{
                            width: "100%",
                            height: "40px",
                            backgroundColor: "#e5e7eb",
                            borderRadius: "6px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                        <div
                          style={{
                            width: "100%",
                            height: "40px",
                            backgroundColor: "#e5e7eb",
                            borderRadius: "6px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      </div>
                    ) : (
                      <div className="form-field-client-btns">
                        <button
                          type="button"
                          className="delete-client"
                          onClick={handleDelete}
                          disabled={isSubmitLoading}
                        >
                          <img src={Delete} alt="Delete" /> Delete
                        </button>
                        <Modal
                          className="delete-modal"
                          isOpen={isDeleteModalOpen}
                          onRequestClose={!isSubmitLoading ? handleCancelDelete : undefined}
                          contentLabel="Confirm Delete"
                          style={{
                            content: {
                              height: "41%",
                              width: "30%",
                              position: "fixed",
                              top: "36%",
                              left: "50%",
                              parent: document.querySelector(".admin-outer.time.tracker"),
                            },
                          }}
                        >
                          <div className="delete-modal-content">
                            <h2>Confirm Delete</h2>
                            <p>Are you sure you want to delete this document category?</p>
                            {isSubmitLoading ? (
                              <div className="delete-modal-buttons" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                                <div
                                  style={{
                                    width: "80px",
                                    height: "40px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "6px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                                <div
                                  style={{
                                    width: "80px",
                                    height: "40px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "6px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="delete-modal-buttons">
                                <button className="delete" onClick={handleConfirmDelete}>
                                  Delete
                                </button>
                                <button onClick={handleCancelDelete}>
                                  Cancel
                                </button>
                              </div>
                            )}
                          </div>
                        </Modal>
                        <button
                          type="submit"
                          className="save-changes"
                          disabled={isSubmitLoading}
                        >
                          Save Changes
                        </button>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </Modal>
          </div>
        </div>
      </div>
    </>
  );
};

export default DocumentCategory;